import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import useTags from 'api/useTags';
import Decimal from 'decimal.js';
import { appearanceUtils } from 'utils/scheduleUtils';

import useImages from '../../../api/useImages';
import useOffers from '../../../api/useOffers';
import useOffersUI from '../../../api/useOffersUI';
import { useProduct } from '../../../api/useProduct';
import useSegments from '../../../api/useSegments';
import useUsers from '../../../api/useUsers';
import {
  Offer,
  OfferUI,
  PopUpProductSequence,
  PopUpSequenceProduct,
  ProductsSequence
} from '../../../common/contracts';
import FormHeader from '../../../components/FormHeader/FormHeader';
import { cn } from '../../../components/lib/utils';
import { Form } from '../../../components/ui';
import { MIN_USD_PRICE } from '../../../constants/constants';
import {
  EAppearanceStatus,
  EFeatureFlag,
  EPopupSubType,
  ESaleDiscountDisplayType,
  OfferType
} from '../../../constants/enums';
import { EToasterStatus, showToast } from '../../../utils/showToast';
import { OfferDesignTypes } from '../../OffersUI/OffersUIForm/types';

import {
  checkUniqueness,
  DEFAULT_OFFER_DESIGN_EXTERNAL_ID,
  DEFAULT_VALUES,
  FORM_SCHEMA,
  PopupsFormFields
} from './constants';
import { usePopupProducts } from './hooks/usePopupProducts';
import GeneralSection from './Sections/GeneralSection';
import PreviewSection from './Sections/PreviewSection';
import ProductsSection from './Sections/ProductsSection';
import SaleAndDiscountsSection from './Sections/SaleAndDiscountsSection';
import SegmentSection from './Sections/SegmentSection';
import TagsSection from './Sections/TagsSection';
import TriggersSection from './Sections/TriggersSection';

type PopupsFormV2Props = {
  edit?: boolean;
  dup?: boolean;
};

const PopupsFormV2: FC<PopupsFormV2Props> = ({ edit = false, dup = false }) => {
  const { publisherId, popupId } = useParams();

  const { fetchFeatureFlags } = useUsers({ publisherId });
  const hasFeatureFlagTags =
    fetchFeatureFlags.data?.featureFlags?.[EFeatureFlag.DASHBOARD_TAGS];
  const hasFeatureFlagPostPurchase =
    fetchFeatureFlags.data?.featureFlags?.[
      EFeatureFlag.DASHBOARD_POST_PURCHASE
    ];

  const navigate = useNavigate();
  const { getProducts } = useProduct(publisherId);
  const {
    addPopUpOffer,
    updatePopUpOffer,
    getOffer,
    getOffers,
    formatProductQuantity
  } = useOffers({ offerId: popupId, offerType: OfferType.POPUP });

  const { getImages } = useImages(publisherId);
  const { getOffersUI } = useOffersUI(publisherId);
  const { getSegments } = useSegments(publisherId);
  const { getTags } = useTags(publisherId, hasFeatureFlagTags);

  const [isPreview, setIsPreview] = useState(false);
  const [segment, setSegment] = useState('');
  const [tag, setTag] = useState('');
  const [originalPostPurchaseValues, setOriginalPostPurchaseValues] = useState<{
    price: number;
    setAsFree: boolean;
  } | null>(null);

  const BUTTON_TEXT = edit ? 'Update' : 'Save';
  const offers = useMemo(
    () => getOffers.data?.offers || [],
    [getOffers.data?.offers]
  );
  const currentOffer = getOffer.data?.result;

  const { getOffers: getBundleOffers } = useOffers({
    offerType: OfferType.BUNDLE
  });

  const { getOffers: getPromotions } = useOffers({
    offerType: OfferType.SPECIAL_OFFER
  });

  const form = useForm<PopupsFormFields>({
    resolver: zodResolver(FORM_SCHEMA),
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES
  });

  const isDailyBonus = form.watch('subType') === EPopupSubType.DAILY_BONUS;
  const {
    productsList,
    addProduct,
    deleteProduct,
    updateProduct,
    setDailyProducts,
    setPostProducts
  } = usePopupProducts(isDailyBonus);

  const isNameTaken = !checkUniqueness({
    value: form.watch('name'),
    offers,
    field: 'name',
    isEdit: edit,
    currentValue: edit ? currentOffer?.name : undefined
  });
  const isPublisherOfferIdTaken = !checkUniqueness({
    value: form.watch('publisherOfferId'),
    offers,
    field: 'publisherOfferId',
    isEdit: edit,
    currentValue: edit ? currentOffer?.publisherOfferId : undefined
  });

  const offerDesignOptions = useMemo(() => {
    if (getOffersUI.isLoading || !getOffersUI.data?.result) return [];

    return getOffersUI.data.result
      .filter((oi: OfferUI) => oi.offerUiType === OfferDesignTypes.PopUp)
      .map((offerUI: OfferUI) => ({
        label: offerUI.name,
        value: offerUI._id,
        externalId: offerUI.externalId
      }));
  }, [getOffersUI.isLoading, getOffersUI.data?.result]);

  const defaultOfferUiId = useMemo(() => {
    return (
      offerDesignOptions.find(
        (option: OfferUI) =>
          option.externalId === DEFAULT_OFFER_DESIGN_EXTERNAL_ID
      )?.value || ''
    );
  }, [offerDesignOptions]);

  useEffect(() => {
    form.register('products');
  }, [form, form.register]);

  const currentOfferUI = useWatch({
    control: form.control,
    name: 'offerUiId',
    defaultValue: defaultOfferUiId
  });

  const handleSubTypeChange = useCallback(
    (newSubType: EPopupSubType) => {
      if (newSubType === EPopupSubType.DAILY_BONUS) {
        if (!originalPostPurchaseValues) {
          setOriginalPostPurchaseValues({
            price: form.getValues('price'),
            setAsFree: form.getValues('setAsFree')
          });
        }
        form.setValue('price', 0, { shouldDirty: true, shouldValidate: true });
        form.setValue('setAsFree', false, {
          shouldDirty: true,
          shouldValidate: true
        });
      } else {
        if (originalPostPurchaseValues) {
          form.setValue('setAsFree', originalPostPurchaseValues.setAsFree, {
            shouldDirty: true,
            shouldValidate: true
          });
          form.setValue(
            'price',
            originalPostPurchaseValues.setAsFree
              ? 0
              : Math.max(originalPostPurchaseValues.price, MIN_USD_PRICE),
            {
              shouldDirty: true,
              shouldValidate: true
            }
          );
        } else {
          form.setValue('setAsFree', false, {
            shouldDirty: true,
            shouldValidate: true
          });
          form.setValue('price', MIN_USD_PRICE, {
            shouldDirty: true,
            shouldValidate: true
          });
        }
      }
    },
    [form, originalPostPurchaseValues]
  );

  const prepareInitialFormValues = (
    result: Offer,
    dup: boolean,
    edit: boolean,
    DEFAULT_VALUES: any,
    defaultOfferUiId: string
  ) => {
    const priceCents = result.productsSequence?.[0]?.priceInUsdCents ?? 0;
    const isDailySubType = result.subType === EPopupSubType.DAILY_BONUS;

    const productsSequence = result.productsSequence as PopUpProductSequence[];

    const products = productsSequence
      .filter((productSequence) => productSequence.products.length > 0)
      .map((productSequence) => {
        const product = productSequence.products[0] as PopUpSequenceProduct;
        return {
          productId: product.product?.productId ?? '',
          quantity: product.quantity ?? 0,
          displayName: product.product?.displayName ?? '',
          image: product.product?.images?.[0]?.url ?? '',
          quantityDisplay: product?.quantityDisplay
        };
      });

    const formValues = {
      ...DEFAULT_VALUES,
      ...result,
      active: dup || edit ? result.active : true,
      name: dup ? `${result.name}_copy` : result.name,
      displayName: `${dup ? result.displayName ?? result.name : result.displayName ?? result.name}${dup ? '_copy' : ''}`,
      publisherOfferId: dup
        ? `${result.publisherOfferId}_copy`
        : result.publisherOfferId,
      offerUiId:
        result.offerUi?._id || result.offerUi?.offerUiId || defaultOfferUiId,
      price: isDailySubType ? 0 : new Decimal(priceCents).div(100).toNumber(),
      setAsFree: isDailySubType ? false : priceCents === 0,
      products,
      discount: result.priceDiscount?.discount ?? DEFAULT_VALUES.discount,
      priceDiscountType:
        result.priceDiscount?.type ?? DEFAULT_VALUES.priceDiscountType,
      coolDownInHours: result.coolDownInHours || 0,
      triggersFrequency: isDailySubType ? 1 : result.triggers[0].every,
      triggersOfferExternalIds: result.triggers[0]?.rules
        ? result.triggers[0]?.rules[0]?.value
        : []
    };

    return { isDailySubType, products, formValues };
  };

  useEffect(() => {
    if (!popupId && defaultOfferUiId) {
      form.setValue('offerUiId', defaultOfferUiId, {
        shouldDirty: false,
        shouldValidate: true
      });
    }

    if ((edit || dup) && getOffer.data) {
      const initial = prepareInitialFormValues(
        getOffer.data.result,
        dup,
        edit,
        DEFAULT_VALUES,
        defaultOfferUiId
      );
      setDailyProducts(initial.isDailySubType ? initial.products : []);
      setPostProducts(initial.isDailySubType ? [] : initial.products);
      form.reset(initial.formValues);
    }
  }, [
    edit,
    dup,
    getOffer.data,
    form,
    popupId,
    defaultOfferUiId,
    setDailyProducts,
    setPostProducts
  ]);

  const onSubmit = async (data: PopupsFormFields) => {
    const priceInUsdCents = data.setAsFree
      ? 0
      : new Decimal(data.price).mul(100).toNumber();
    let productsSequence: ProductsSequence[] = productsList.map(
      (product, index) => ({
        index: index + 1,
        priceInUsdCents,
        products: [
          {
            productId: product.productId,
            publisherProductId: product.publisherProductId,
            quantity: product.quantity
          }
        ]
      })
    );

    if (productsSequence.length === 0) {
      productsSequence = [
        {
          index: 1,
          priceInUsdCents,
          products: []
        }
      ];
    }

    const payload: Partial<Offer> & { sectionId?: string | null } = {
      active: data.active,
      coolDownInHours: 1,
      ...(data.description && { description: data.description }),
      displayName: data.displayName || data.name,
      name: data.name,
      offerUiId: data.offerUiId || data._id,
      priceDiscount: {
        discount: data.discount,
        type: data.priceDiscountType || ESaleDiscountDisplayType.PERCENTAGE
      },
      priority: data.priority,
      productsSequence,
      publisherOfferId: data.publisherOfferId,
      segments: data.segments,
      showAfter: isDailyBonus ? 'login' : 'purchase',
      startOver: isDailyBonus ? data.startOver : true,
      subType: data.subType,
      tags: data.tags,
      triggers: isDailyBonus
        ? [
            {
              type: 'time',
              eventName: 'impression',
              every: 1440
            }
          ]
        : [
            {
              type: 'event',
              eventName: 'purchase',
              every: data.triggersFrequency || 1,
              rules:
                data.triggersOfferExternalIds &&
                data.triggersOfferExternalIds.length > 0
                  ? [
                      {
                        fieldName: 'publisherOfferId',
                        operator: 'in',
                        value: data.triggersOfferExternalIds
                      }
                    ]
                  : []
            }
          ],
      type: data.type
    };

    if (edit && popupId && !dup) {
      updatePopUpOffer.mutate(
        {
          offerId: popupId,
          form: payload
        },
        {
          onSuccess: () => {
            showToast({
              message: 'Popup updated successfully',
              status: EToasterStatus.SUCCESS
            });
            getOffers.refetch();
            navigate('../');
          },
          onError: () => {
            showToast({
              message: 'Error updating popup',
              status: EToasterStatus.ERROR
            });
          }
        }
      );
    } else {
      addPopUpOffer.mutate(payload, {
        onSuccess: () => {
          showToast({
            message: 'Popup added successfully',
            status: EToasterStatus.SUCCESS
          });
          getOffers.refetch();
          navigate('../');
        },
        onError: () => {
          showToast({
            message: 'Error adding popup',
            status: EToasterStatus.ERROR
          });
        }
      });
    }
  };

  const offersIds = useMemo(() => {
    const extractActiveOffers = (offers: Offer[] = []) =>
      offers
        .filter((offer) => {
          const isActive = offer.active;
          const scheduleStatus = appearanceUtils.getStrongestStatusWithTime(
            offer?.schedule?.timeFrames
          )?.status;

          const isExpiredPromotion =
            offer.type === OfferType.SPECIAL_OFFER &&
            scheduleStatus === EAppearanceStatus.ENDED &&
            !offer?.schedule?.permanent;

          return isActive && !isExpiredPromotion;
        })
        .map((offer) => ({
          value: offer.publisherOfferId,
          label: offer.publisherOfferId,
          content: offer.publisherOfferId
        }));

    const bundleOffers = extractActiveOffers(
      getBundleOffers?.data?.offers || []
    );
    const promotionOffers = extractActiveOffers(
      getPromotions?.data?.offers || []
    );
    return [...bundleOffers, ...promotionOffers];
  }, [getBundleOffers?.data?.offers, getPromotions?.data?.offers]);

  return (
    <div className="bg-white fixed h-screen w-full top-0 left-0 z-[1000] overflow-scroll">
      <FormHeader
        title={
          edit
            ? `Edit Popups Offer (${form.watch('name') || currentOffer?.name || ''})`
            : dup
              ? 'Duplicate Popups Offer'
              : 'Create Popups Offer'
        }
        buttonText={BUTTON_TEXT}
        onCloseForm={() => navigate('../')}
        onSubmitForm={() => form.handleSubmit(onSubmit)()}
        secondaryButtonText={isPreview ? 'Hide Preview' : 'Preview'}
        onSecondaryButtonClick={() => setIsPreview(!isPreview)}
        isFormValid={
          form.formState.isValid &&
          (dup || form.formState.isDirty) &&
          !isNameTaken &&
          !isPublisherOfferIdTaken
        }
      />
      {!getImages.isLoading && getImages.data && (
        <Form {...form}>
          <div className={cn('w-full', isPreview && 'grid grid-cols-2')}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="mt-7 mb-9 w-[456px] mx-auto flex flex-col gap-y-9"
            >
              <GeneralSection
                control={form.control}
                currentOfferUiId={form.watch('offerUiId')}
                currentPriority={form.watch('priority')}
                offerDesignOptions={offerDesignOptions}
                isDailyBonus={isDailyBonus}
                onSubTypeChange={handleSubTypeChange}
                hasFeatureFlagPostPurchase={hasFeatureFlagPostPurchase}
                isNameTaken={isNameTaken}
                isPublisherOfferIdTaken={isPublisherOfferIdTaken}
              />
              <SegmentSection
                segment={segment}
                setSegment={setSegment}
                getValues={form.getValues}
                setValue={form.setValue}
                currentSegments={form.watch('segments')}
                segmentOptions={getSegments.data?.data || []}
              />
              {hasFeatureFlagTags && (
                <TagsSection
                  tag={tag}
                  setTag={setTag}
                  getValues={form.getValues}
                  setValue={form.setValue}
                  currentTags={form.watch('tags')}
                  tagOptions={getTags.data?.data || []}
                />
              )}
              {!isDailyBonus && (
                <SaleAndDiscountsSection
                  control={form.control}
                  currentPriceDiscount={form.watch('priceDiscountType')}
                />
              )}
              <ProductsSection
                form={form}
                isDailyBonus={isDailyBonus}
                formatProductQuantity={formatProductQuantity}
                products={getProducts}
                productsList={productsList}
                addProduct={addProduct}
                deleteProduct={deleteProduct}
                updateProduct={updateProduct}
              />
              {!isDailyBonus && (
                <TriggersSection control={form.control} offersIds={offersIds} />
              )}
            </form>
            {isPreview && (
              <PreviewSection
                publisherId={publisherId}
                currentOfferUI={currentOfferUI}
                isDailyBonus={isDailyBonus}
                currentPrice={form.watch('price')}
                products={getProducts.data}
                productsList={productsList}
              />
            )}
          </div>
        </Form>
      )}
    </div>
  );
};

export default PopupsFormV2;
